"use client";

import { ChevronDownIcon, Pencil1Icon, PlusCircledIcon } from "@radix-ui/react-icons";
import { Button, Dialog, DialogTitle, DropdownMenu, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { useTNsViaAccountIdQuery } from "@/app/account-settings/utils";
import { AuthProvider } from "@/auth-types";
import { TelephoneNumberInfo } from "@/clients/attnow/types";
import RequestUbidDialog from "@/components/RequestUbidDialog";
import { useAccountsQuery } from "@/hooks/useAccountsQuery";
import useTNStore from "@/stores/TNStore";
import useUBStore from "@/stores/UBStore";
import useVFCCSessionStore from "@/stores/VFCCSessionStore";
import { formattedTN, TN_REQUIRED_LENGTH } from "@/utils/general";

interface TNSelectProps {
    tn?: string;
    onSelect: (tn: TelephoneNumberInfo) => void;
    tns: TelephoneNumberInfo[];
}

function TNSelect({ tn, onSelect, tns }: TNSelectProps) {
    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger>
                <Button color="gray" variant="surface" className="min-w-full h-12 p-3 rounded-lg justify-between">
                    {tn ? formattedTN(tn) : "Select a phone number"}
                    <ChevronDownIcon width={24} height={24} />
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
                {tns.map((tnInfo) => (
                    <DropdownMenu.Item className="justify-start gap-2 p-2 h-10" key={tnInfo.tn} onSelect={() => onSelect(tnInfo)}>
                        {formattedTN(tnInfo.tn)}
                    </DropdownMenu.Item>
                ))}
            </DropdownMenu.Content>
        </DropdownMenu.Root>
    );
}

interface HaloProps {
    tn: string;
    setTN: (tn: string) => void;
}

function HaloB({ tn, setTN }: HaloProps) {
    const { vfccSession } = useVFCCSessionStore();
    const accountsQueryResult = useAccountsQuery(AuthProvider.HaloB, vfccSession);
    const tns = vfccSession.tnInfoList || accountsQueryResult.data?.accounts.flatMap((account) => account.tns);
    if (!tns) {
        if (accountsQueryResult.isFetching || accountsQueryResult.isLoading)
            return (
                <Text as="div" size="2" mb="1" weight="bold">
                    Loading phone numbers...
                </Text>
            );
        return (
            <Text as="div" size="2" mb="1" weight="bold">
                No phone numbers found on account. Please try signing in again or return to Business Center
            </Text>
        );
    }
    return <TNSelect tn={tn} onSelect={(tnInfo) => setTN(tnInfo.tn)} tns={tns} />;
}

function HaloE({ tn, setTN }: HaloProps) {
    const { ubid } = useUBStore();
    const { vfccSession } = useVFCCSessionStore();
    const accountsQueryResult = useTNsViaAccountIdQuery(ubid);
    const tns = vfccSession.tnInfoList || accountsQueryResult.data;
    const [forceInput, setForceInput] = useState<boolean>(false);
    const [ubidDialogOpen, setUbidDialogOpen] = useState<boolean>(false);

    const handleTNChange = (phoneNumber: string) => {
        setTN(phoneNumber);
    };

    return (
        <>
            <RequestUbidDialog open={ubidDialogOpen} setOpen={setUbidDialogOpen} />
            <Flex justify="start">
                <Button variant="ghost" onClick={() => setUbidDialogOpen(true)}>
                    {!ubid ? <PlusCircledIcon /> : <Pencil1Icon />}
                    {!ubid ? "Add account (Required for verification)" : `Change account (${ubid})`}
                </Button>
            </Flex>
            <label className="min-w-full">
                <Text as="div" size="2" mb="1" weight="bold">
                    Telephone Number
                </Text>
                {forceInput || !tns?.length ? (
                    <Flex direction="column" gap="1" align="stretch">
                        <PhoneInput
                            country={"us"}
                            onlyCountries={["us"]}
                            value={tn}
                            onChange={handleTNChange}
                            data-testid="my-tn"
                            inputProps={{ "data-testid": "my-tn" }}
                            placeholder={"+_ (___) ___-____"}
                            containerClass="min-w-full"
                            specialLabel=""
                            inputClass="!h-12 min-w-full rounded-lg disabled:cursor-wait"
                            disabled={!forceInput && (accountsQueryResult.isFetching || accountsQueryResult.isLoading)}
                        />
                        {!!tns?.length && (
                            <Flex justify="end">
                                <Button variant="ghost" onClick={() => setForceInput(false)}>
                                    View phone numbers on account
                                </Button>
                            </Flex>
                        )}
                    </Flex>
                ) : (
                    <Flex direction="column" gap="1" align="end">
                        <TNSelect tn={tn} onSelect={(tnInfo) => setTN(tnInfo.tn)} tns={tns} />
                        <Button variant="ghost" onClick={() => setForceInput(true)}>
                            Enter a different phone number
                        </Button>
                    </Flex>
                )}
            </label>
        </>
    );
}

export interface RequestTNDialogProps {
    open?: boolean;
    setOpen?: (open: boolean) => void;
}

function RequestTNDialog({ open, setOpen }: RequestTNDialogProps) {
    const { data: authSession } = useSession();
    const { tn, setTN } = useTNStore();
    const [txtTN, setTxtTN] = useState<string>(tn);

    useEffect(() => {
        setTxtTN(tn);
    }, [tn, open]);

    const handleNext = () => {
        setTN(txtTN);
        if (setOpen) setOpen(false);
    };

    return (
        <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Content className="DialogContent">
                <VisuallyHidden>
                    <DialogTitle>Request TN</DialogTitle>
                </VisuallyHidden>
                <Flex gap="5" direction="column" align="stretch">
                    {open && authSession?.user.provider === AuthProvider.HaloB && <HaloB tn={txtTN} setTN={setTxtTN} />}
                    {open && authSession?.user.provider === AuthProvider.HaloE && <HaloE tn={txtTN} setTN={setTxtTN} />}
                    {!authSession && (
                        <Text as="div" size="2" mb="1" weight="bold">
                            Please sign in to change the phone number
                        </Text>
                    )}
                    <Flex gap="3" justify="end" align="center">
                        <Button variant="ghost" onClick={() => setOpen && setOpen(false)} className="px-6 py-3 rounded-full cursor-pointer">
                            Dismiss
                        </Button>
                        {!!authSession && (
                            <Button
                                variant="surface"
                                onClick={handleNext}
                                disabled={txtTN.length !== TN_REQUIRED_LENGTH}
                                className="px-6 py-3 h-10 rounded-full cursor-pointer disabled:text-gray-400 disabled:cursor-default">
                                Update
                            </Button>
                        )}
                    </Flex>
                </Flex>
            </Dialog.Content>
        </Dialog.Root>
    );
}

export default RequestTNDialog;
